/**
 * TransformationInsightCard - Layer 3 Premium Component (DramaX)
 *
 * Editorial card for showcasing before/after transformation insights
 * Visible to DramaX (Layer 3) subscribers only
 *
 * Features:
 * - 12-column editorial grid layout with responsive 2-column before/after
 * - Black background with gold accents (#C8A882)
 * - Miller Display typography (editorial serif for Layer 3)
 * - 500ms dramatic-reveal-animation
 * - Qualitative impact scoring (Low/Moderate/High)
 * - Smart text truncation at sentence boundaries
 * - Y2K Phase 2 (P3): Multiple bubble tails for group dynamics transformations
 * - Y2K Phase 3 (P4): Light Skeuomorphism neumorphic shadows (inset + outset)
 *
 * Grid Structure:
 * - Header: Transformation type (8 cols) + Share CTA (4 cols)
 * - Before/After: 2-column responsive (6+6 desktop, 12+12 mobile)
 * - Insights: Full width bullet list (12 cols)
 * - Impact Badge: Left-aligned (6 cols)
 *
 * A/B Testing (PostHog):
 * - Experiment: neumorphic_shadows_tier3
 * - Control (50%): No neumorphic shadows (remove shadow-neumorphic-* classes)
 * - Treatment (50%): Neumorphic shadows enabled (current implementation)
 * - Track: Component_Render_TransformationCard { has_neumorphic_shadow: boolean }
 * - Metrics: engagement_rate, time_on_card, share_button_ctr
 * - Collection Period: 1 week (min 1,000 sessions per variant)
 *
 * Refactored: 2025-11-21 (Line Count: 450 → 247 lines)
 */

import React, { useEffect, useState } from 'react';
import { View, Pressable, AccessibilityInfo } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { TierCard } from './TierCard';
import { TierHeadline, TierBody } from './TierTypography';
import { MultipleBubbleTails } from './transformation/BubbleTails';
import {
  truncateAtSentence,
  getImpactLabel,
  getImpactBadgeStyles,
  isGroupDynamicsTransformation,
  type TransformationInsightCardProps,
} from '../lib/transformation/transformationUtils';

export type { TransformationInsightCardProps };

export function TransformationInsightCard({
  transformationType,
  beforeText,
  afterText,
  insights,
  impactScore,
  tierLevel,
  onShare,
  className = '',
  testID = 'transformation-insight-card',
}: TransformationInsightCardProps) {
  const { t } = useTranslation(['features', 'common']);
  const [isReducedMotion, setReducedMotion] = useState(false);

  // Y2K Phase 2 (P3): Check for group dynamics transformation
  const showBubbleTails = isGroupDynamicsTransformation(transformationType);

  // Reduced motion support
  useEffect(() => {
    const checkReduceMotion = async () => {
      setReducedMotion(await AccessibilityInfo.isReduceMotionEnabled());
    };
    checkReduceMotion();

    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReducedMotion
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // Tier visibility check - Layer 3+ only (DramaX)
  // Guard against undefined/NaN: `undefined < 3` is false in JS (NaN comparison),
  // which would bypass this check and show DramaX content to unauthenticated users.
  if (!tierLevel || tierLevel < 3) {
    return null;
  }

  // Validate insights (minimum 1 required)
  if (!insights || insights.length === 0) {
    console.warn('[TransformationInsightCard] No insights provided. Minimum 1 required.');
    return null;
  }

  const truncatedBefore = truncateAtSentence(beforeText);
  const truncatedAfter = truncateAtSentence(afterText);
  const impactLabel = getImpactLabel(impactScore);
  const impactBadgeStyles = getImpactBadgeStyles(impactLabel);

  // Try to get translated transformation type, fallback to raw value
  const translatedType = t(`transformationInsightCard.types.${transformationType}`, {
    defaultValue: transformationType,
  });

  return (
    <View style={{ position: 'relative' }}>
      {/* Y2K Phase 2 (P3): Multiple bubble tails for group dynamics transformations */}
      {showBubbleTails && <MultipleBubbleTails isReducedMotion={isReducedMotion} />}

      <TierCard
        variant="premium"
        className={`bg-black shadow-neumorphic-inset-gold dramatic-reveal-animation ${className}`}
        testID={testID}
      >
        {/* 12-column grid container */}
        <View className="grid grid-cols-12 gap-4">
        {/* Header Row: Transformation Type (8 cols) + Share CTA (4 cols) */}
        <View className="col-span-8">
          <TierHeadline
            size="lg"
            weight="bold"
            color="text-white"
            testID={`${testID}-transformation-type`}
          >
            {translatedType}
          </TierHeadline>
        </View>

        {onShare && (
          <View className="col-span-4 flex items-end justify-start">
            <Pressable
              onPress={onShare}
              className="px-4 py-2 border border-accent-gold rounded-tier bg-transparent active:bg-accent-gold/20 shadow-neumorphic-outset-gold transition-colors"
              testID={`${testID}-share-button`}
              accessibilityLabel={t('transformationInsightCard.accessibility.shareTransformation' as any, {
                transformationType: translatedType,
              })}
            >
              <TierBody size="base" weight="medium" color="text-accent-gold">
                {t('common:share' as any)}
              </TierBody>
            </Pressable>
          </View>
        )}

        {/* Before/After Section: Responsive 2-column (6+6 desktop, 12+12 mobile) */}
        <View className="col-span-12 md:col-span-6">
          <TierBody size="sm" weight="semibold" color="text-accent-gold" className="mb-2">
            {t('transformationInsightCard.labels.before' as any)}
          </TierBody>
          <View
            className="p-4 bg-gray-900 border-l-2 border-red-500 rounded-tier"
            accessibilityLabel={t('transformationInsightCard.accessibility.beforeText' as any)}
          >
            <TierBody
              size="base"
              color="text-gray-300"
              className="leading-relaxed"
              testID={`${testID}-before-text`}
            >
              {truncatedBefore}
            </TierBody>
          </View>
        </View>

        <View className="col-span-12 md:col-span-6">
          <TierBody size="sm" weight="semibold" color="text-accent-gold" className="mb-2">
            {t('transformationInsightCard.labels.after' as any)}
          </TierBody>
          <View
            className="p-4 bg-gray-900 border-l-2 border-green-500 rounded-tier"
            accessibilityLabel={t('transformationInsightCard.accessibility.afterText' as any)}
          >
            <TierBody
              size="base"
              color="text-gray-100"
              className="leading-relaxed"
              testID={`${testID}-after-text`}
            >
              {truncatedAfter}
            </TierBody>
          </View>
        </View>

        {/* Insights Section: Full width (12 cols) */}
        <View className="col-span-12">
          <TierBody size="sm" weight="semibold" color="text-accent-gold" className="mb-2">
            {t('transformationInsightCard.insights_label' as any)}
          </TierBody>
          <View className="space-y-2">
            {insights.slice(0, 3).map((insight, index) => (
              <View key={index} className="flex flex-row">
                <TierBody size="base" color="text-accent-gold" className="mr-2">
                  •
                </TierBody>
                <TierBody
                  size="base"
                  color="text-gray-200"
                  className="flex-1"
                  testID={`${testID}-insight-${index}`}
                >
                  {insight}
                </TierBody>
              </View>
            ))}
          </View>
        </View>

        {/* Impact Badge: Left-aligned (6 cols) */}
        <View className="col-span-6">
          <View
            className={`inline-flex items-center px-3 py-1 border-l-2 rounded-tier ${impactBadgeStyles}`}
          >
            <TierBody
              size="sm"
              weight="semibold"
              color="text-gray-900"
              testID={`${testID}-impact-badge`}
            >
              {t(`transformationInsightCard.impact.${impactLabel}` as any)}
            </TierBody>
          </View>
        </View>
      </View>
    </TierCard>
    </View>
  );
}
